import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

const COLORS = {
  primary: '#2D5016',
  primaryLight: '#4A7C2B',
  secondary: '#8BA888',
  background: '#F5F7F3',
  text: '#2D3E1F',
  textLight: '#6B7F5C',
  border: '#D4E0CE',
  white: '#FFFFFF',
  error: '#C44536',
};

const SUPABASE_FUNCTIONS_URL = 'https://ztbeipyycwswknnzqyze.supabase.co/functions/v1';

const CompleteSignup = ({ token: tokenProp }) => {
  // Get token from either prop (new /join/token route) or URL param (old ?token= route)
  const [searchParams] = useSearchParams();
  const token = tokenProp || searchParams.get('token');

  const [step, setStep] = useState(1); // 1 = verify code, 2 = questions
  const [verificationCode, setVerificationCode] = useState('');
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadingQuestions, setLoadingQuestions] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [tokenError, setTokenError] = useState(false);

  useEffect(() => {
    if (!token) {
      setTokenError(true);
    }
  }, [token]);

  useEffect(() => {
    if (step === 2 && questions.length === 0) {
      loadQuestions();
    }
  }, [step]);

  const loadQuestions = async () => {
    setLoadingQuestions(true);
    // 15 questions - removed conflict_style, inside_world, describe_yourself, core_value, desired_habit, skill_to_master
    const hardcodedQuestions = [
      // RELATIONSHIP BASICS
      { question_key: 'relationship_goal', question_text: "What's your goal for Virbal?", input_type: 'select', options: ["Go deeper", "Stay close through a busy season", "Build the habit early (new relationship)", "Keep things alive and interesting"], required: true },
      { question_key: 'relationship_length', question_text: 'How long have you been together?', input_type: 'select', options: ["Less than a year", "1–3 years", "4–10 years", "10+ years"], required: true },
      { question_key: 'proximity', question_text: 'Are you in the same city or long-distance?', input_type: 'select_with_followup', options: ["Same city", "Long-distance"], required: true },
      { question_key: 'how_we_met', question_text: 'How did you first meet?', input_type: 'text', required: true },
      { question_key: 'important_dates', question_text: 'What are the dates that matter most to you two? i.e. anniversaries, birthdays, major events', input_type: 'important_dates', required: false },
      
      // THE RELATIONSHIP ITSELF
      { question_key: 'unappreciated_actions', question_text: "What's one thing your partner does that you are especially grateful for?", input_type: 'textarea', required: true },
      { question_key: 'love_languages_receive', question_text: 'How do you prefer to feel loved?', input_type: 'multiselect', options: ["Words of Affirmation - verbal expressions of love, appreciation, and encouragement", "Acts of Service - your partner doing helpful things for you", "Receiving Gifts - receiving meaningful tokens of affection", "Quality Time - receiving undivided, focused attention", "Physical Touch - physical connection like hugs, holding hands, etc."], required: true },
      { question_key: 'love_languages_give', question_text: 'How do you naturally show love?', input_type: 'multiselect', options: ["Words of Affirmation - verbal expressions of love, appreciation, and encouragement", "Acts of Service - doing helpful things for your partner", "Giving Gifts - giving meaningful tokens of affection", "Quality Time - giving undivided, focused attention", "Physical Touch - physical connection like hugs, holding hands, etc."], required: true },
      { question_key: 'time_spend', question_text: 'When you two have free time together, what does it usually look like?', input_type: 'select', options: ["We're homebodies", "We like getting out", "Mix of both", "We don't get much free time right now"], required: true },
      { question_key: 'current_challenges', question_text: 'Is there anything you two are navigating together right now - new job, new baby, a tough season, a big transition?', input_type: 'textarea', required: false },
      
      // SHARED HISTORY
      { question_key: 'shared_memory', question_text: "What's a memory you two share that still makes you laugh - or one that still means a lot to you?", input_type: 'textarea', required: false },
      
      // WHO YOU ARE
      { question_key: 'perfect_day', question_text: 'If you could design a perfect day together, what would it look like?', input_type: 'textarea', required: true },
      { question_key: 'feel_alive', question_text: 'What is one activity or moment that makes you feel most alive and energized?', input_type: 'textarea', required: true },
      
      // DREAMS & GROWTH
      { question_key: 'big_goals', question_text: 'What are 2 or 3 big goals or dreams you have for the next 5–10 years?', input_type: 'textarea', required: true },
      { question_key: 'proud_accomplishments', question_text: "What are a few things you're really proud of accomplishing - big or small?", input_type: 'textarea', required: true },
    ];
    setQuestions(hardcodedQuestions);
    setLoadingQuestions(false);
  };

  const handleVerificationSubmit = (e) => {
    e.preventDefault();
    setError('');

    if (!verificationCode || verificationCode.length !== 6) {
      setError('Please enter the 6-digit verification code from your SMS');
      return;
    }

    // Code is valid, move to questions
    setStep(2);
  };

  const handleAnswerChange = (questionKey, value) => {
    setAnswers(prev => ({
      ...prev,
      [questionKey]: value
    }));
  };

  const handleQuestionsSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const requiredQuestions = questions.filter(q => q.required);
    const missingAnswers = requiredQuestions.filter(q => {
      const answer = answers[q.question_key];
      if (!answer) return true;
      
      // Handle arrays (multiselect)
      if (Array.isArray(answer)) return answer.length === 0;
      
      // Handle objects (select_with_followup, important_dates)
      if (typeof answer === 'object') {
        // For select_with_followup, check if selection exists
        if (answer.selection !== undefined) return !answer.selection;
        // For important_dates, check if at least one entry has both label and date
        if (answer.label !== undefined || answer.date !== undefined) {
          return !answer.label || !answer.date;
        }
        return false;
      }
      
      // Handle strings
      return typeof answer === 'string' && answer.trim() === '';
    });
    
    if (missingAnswers.length > 0) {
      setError(`Please answer all required questions (${missingAnswers.length} remaining)`);
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${SUPABASE_FUNCTIONS_URL}/complete-partner-account`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token: token,
          verificationCode: verificationCode,
          partner2Answers: answers,
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to complete signup');
      }

      setSuccess(true);
    } catch (err) {
      console.error('Completion error:', err);
      setError(err.message || 'Failed to complete signup. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const renderQuestionInput = (question) => {
    const value = answers[question.question_key] || '';

    switch (question.input_type) {
      case 'textarea':
        return (
          <textarea
            id={`question-${question.id}`}
            value={value}
            onChange={(e) => handleAnswerChange(question.question_key, e.target.value)}
            placeholder="Your answer..."
            rows="4"
            required={question.required}
            style={{
              width: '100%',
              padding: '0.75rem 1rem',
              border: `2px solid ${COLORS.border}`,
              borderRadius: '6px',
              fontSize: '1rem',
              fontFamily: 'Inter, sans-serif',
              resize: 'vertical',
            }}
          />
        );

      case 'select':
        const options = question.options || [];
        return (
          <select
            id={`question-${question.id}`}
            value={value}
            onChange={(e) => handleAnswerChange(question.question_key, e.target.value)}
            required={question.required}
            style={{
              width: '100%',
              padding: '0.75rem 1rem',
              border: `2px solid ${COLORS.border}`,
              borderRadius: '6px',
              fontSize: '1rem',
              fontFamily: 'Inter, sans-serif',
              cursor: 'pointer',
              appearance: 'none',
              backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%232D5016' d='M6 9L1 4h10z'/%3E%3C/svg%3E")`,
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'right 1rem center',
              paddingRight: '2.5rem',
            }}
          >
            <option value="">Select an option...</option>
            {options.map((option, idx) => (
              <option key={idx} value={option}>{option}</option>
            ))}
          </select>
        );

      case 'date':
        return (
          <input
            type="date"
            id={`question-${question.id}`}
            value={value}
            onChange={(e) => handleAnswerChange(question.question_key, e.target.value)}
            required={question.required}
            style={{
              width: '100%',
              padding: '0.75rem 1rem',
              border: `2px solid ${COLORS.border}`,
              borderRadius: '6px',
              fontSize: '1rem',
              fontFamily: 'Inter, sans-serif',
            }}
          />
        );

      case 'multi_date':
        return (
          <textarea
            id={`question-${question.id}`}
            value={value}
            onChange={(e) => handleAnswerChange(question.question_key, e.target.value)}
            placeholder="Enter dates (e.g., First Date: 2020-01-15, Wedding: 2022-06-10)"
            rows="3"
            required={question.required}
            style={{
              width: '100%',
              padding: '0.75rem 1rem',
              border: `2px solid ${COLORS.border}`,
              borderRadius: '6px',
              fontSize: '1rem',
              fontFamily: 'Inter, sans-serif',
              resize: 'vertical',
            }}
          />
        );

      case 'multiselect':
        const multiselectOptions = question.options || [];
        const selectedValues = Array.isArray(value) ? value : (value ? [value] : []);
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {multiselectOptions.map((option, idx) => (
              <label key={idx} style={{
                display: 'flex',
                alignItems: 'flex-start',
                cursor: 'pointer',
                padding: '0.75rem',
                border: `2px solid ${selectedValues.includes(option) ? COLORS.primary : COLORS.border}`,
                borderRadius: '6px',
                background: selectedValues.includes(option) ? 'rgba(45, 80, 22, 0.05)' : 'transparent',
                transition: 'all 0.2s',
              }}>
                <input
                  type="checkbox"
                  checked={selectedValues.includes(option)}
                  onChange={(e) => {
                    const newValues = e.target.checked
                      ? [...selectedValues, option]
                      : selectedValues.filter(v => v !== option);
                    handleAnswerChange(question.question_key, newValues);
                  }}
                  style={{
                    marginRight: '0.75rem',
                    marginTop: '0.25rem',
                    cursor: 'pointer',
                    width: '18px',
                    height: '18px',
                    accentColor: COLORS.primary,
                  }}
                />
                <span style={{
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '0.95rem',
                  color: COLORS.text,
                  lineHeight: 1.4,
                }}>
                  {option}
                </span>
              </label>
            ))}
          </div>
        );

      case 'important_dates':
        const dates = Array.isArray(value) ? value : (value ? [value] : [{ label: '', date: '' }]);
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {dates.map((dateEntry, idx) => (
              <div key={idx} style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '0.5rem',
                padding: '1rem',
                border: `1px solid ${COLORS.border}`,
                borderRadius: '6px',
                background: 'rgba(255, 255, 255, 0.5)',
              }}>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                  <input
                    type="text"
                    placeholder="e.g., Anniversary, Birthday"
                    value={dateEntry.label || ''}
                    onChange={(e) => {
                      const newDates = [...dates];
                      newDates[idx] = { ...newDates[idx], label: e.target.value };
                      handleAnswerChange(question.question_key, newDates);
                    }}
                    style={{
                      flex: 1,
                      padding: '0.75rem 1rem',
                      border: `2px solid ${COLORS.border}`,
                      borderRadius: '6px',
                      fontSize: '1rem',
                      fontFamily: 'Inter, sans-serif',
                    }}
                  />
                  {dates.length > 1 && (
                    <button
                      type="button"
                      onClick={() => {
                        const newDates = dates.filter((_, i) => i !== idx);
                        handleAnswerChange(question.question_key, newDates);
                      }}
                      style={{
                        padding: '0.75rem 1rem',
                        background: COLORS.error,
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontFamily: 'Inter, sans-serif',
                        fontSize: '0.9rem',
                      }}
                    >
                      Remove
                    </button>
                  )}
                </div>
                <input
                  type="date"
                  value={dateEntry.date || ''}
                  onChange={(e) => {
                    const newDates = [...dates];
                    newDates[idx] = { ...newDates[idx], date: e.target.value };
                    handleAnswerChange(question.question_key, newDates);
                  }}
                  style={{
                    width: '100%',
                    padding: '0.75rem 1rem',
                    border: `2px solid ${COLORS.border}`,
                    borderRadius: '6px',
                    fontSize: '1rem',
                    fontFamily: 'Inter, sans-serif',
                  }}
                />
              </div>
            ))}
            {dates.length < 3 && (
              <button
                type="button"
                onClick={() => {
                  const newDates = [...dates, { label: '', date: '' }];
                  handleAnswerChange(question.question_key, newDates);
                }}
                style={{
                  padding: '0.75rem 1rem',
                  background: 'transparent',
                  color: COLORS.primary,
                  border: `2px solid ${COLORS.primary}`,
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '0.95rem',
                  fontWeight: 600,
                }}
              >
                + Add Another Date
              </button>
            )}
          </div>
        );

      case 'select_with_followup':
        const selectOptions = question.options || [];
        return (
          <div>
            <select
              id={`question-${question.id}`}
              value={value.selection || ''}
              onChange={(e) => handleAnswerChange(question.question_key, { ...value, selection: e.target.value })}
              required={question.required}
              style={{
                width: '100%',
                padding: '0.75rem 1rem',
                border: `2px solid ${COLORS.border}`,
                borderRadius: '6px',
                fontSize: '1rem',
                fontFamily: 'Inter, sans-serif',
                cursor: 'pointer',
                appearance: 'none',
                backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%232D5016' d='M6 9L1 4h10z'/%3E%3C/svg%3E")`,
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'right 1rem center',
                paddingRight: '2.5rem',
                marginBottom: '1rem',
              }}
            >
              <option value="">Select an option...</option>
              {selectOptions.map((option, idx) => (
                <option key={idx} value={option}>{option}</option>
              ))}
            </select>
            {value.selection && (
              <input
                type="text"
                placeholder="Your city..."
                value={value.city || ''}
                onChange={(e) => handleAnswerChange(question.question_key, { ...value, city: e.target.value })}
                required={question.required}
                style={{
                  width: '100%',
                  padding: '0.75rem 1rem',
                  border: `2px solid ${COLORS.border}`,
                  borderRadius: '6px',
                  fontSize: '1rem',
                  fontFamily: 'Inter, sans-serif',
                }}
              />
            )}
          </div>
        );

      default:
        return (
          <input
            type="text"
            id={`question-${question.id}`}
            value={value}
            onChange={(e) => handleAnswerChange(question.question_key, e.target.value)}
            placeholder="Your answer..."
            required={question.required}
            style={{
              width: '100%',
              padding: '0.75rem 1rem',
              border: `2px solid ${COLORS.border}`,
              borderRadius: '6px',
              fontSize: '1rem',
              fontFamily: 'Inter, sans-serif',
            }}
          />
        );
    }
  };

  if (tokenError || !token) {
    return (
      <div style={{
        minHeight: '100vh',
        background: COLORS.background,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '2rem 1rem',
        fontFamily: 'Georgia, serif',
      }}>
        <div style={{
          maxWidth: '500px',
          background: COLORS.white,
          borderRadius: '8px',
          padding: '3rem 2rem',
          textAlign: 'center',
          boxShadow: '0 2px 8px rgba(45, 80, 22, 0.08)',
        }}>
          <img 
            src="https://static.wixstatic.com/media/e78d43_e6a2a4d725014972851fe9dd56aeaa4b~mv2.png"
            alt="Virbal"
            style={{ maxWidth: '200px', marginBottom: '2rem' }}
          />
          <h2 style={{ color: COLORS.text, marginBottom: '1rem', fontWeight: 400 }}>Invalid Link</h2>
          <p style={{ color: COLORS.textLight, fontFamily: 'Inter, sans-serif' }}>
            This signup link is invalid or has expired. Please contact your partner for a new link.
          </p>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div style={{
        minHeight: '100vh',
        background: COLORS.background,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '2rem 1rem',
        fontFamily: 'Georgia, serif',
      }}>
        <div style={{
          maxWidth: '500px',
          background: COLORS.white,
          borderRadius: '8px',
          padding: '3rem 2rem',
          textAlign: 'center',
          boxShadow: '0 2px 8px rgba(45, 80, 22, 0.08)',
        }}>
          <img 
            src="https://static.wixstatic.com/media/e78d43_e6a2a4d725014972851fe9dd56aeaa4b~mv2.png"
            alt="Virbal"
            style={{ maxWidth: '200px', marginBottom: '2rem' }}
          />
          <div style={{
            width: '80px',
            height: '80px',
            borderRadius: '50%',
            background: COLORS.primary,
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '3rem',
            margin: '0 auto 2rem',
          }}>✓</div>
          <h2 style={{ fontSize: '2rem', color: COLORS.text, marginBottom: '1.5rem', fontWeight: 400 }}>
            Welcome to Virbal!
          </h2>
          <p style={{
            color: COLORS.textLight,
            fontFamily: 'Inter, sans-serif',
            fontSize: '1.1rem',
            lineHeight: 1.6,
            marginBottom: '1rem',
          }}>
            Your account is now active! You'll both start receiving daily conversation prompts.
          </p>
          <p style={{
            color: COLORS.textLight,
            fontFamily: 'Inter, sans-serif',
            fontSize: '1.1rem',
            lineHeight: 1.6,
          }}>
            Check your phones for a welcome message!
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: COLORS.background,
      padding: '2rem 1rem',
      fontFamily: 'Georgia, serif',
    }}>
      <div style={{
        maxWidth: '650px',
        margin: '0 auto',
        background: COLORS.white,
        borderRadius: '8px',
        padding: '3rem 2.5rem',
        boxShadow: '0 2px 8px rgba(45, 80, 22, 0.08)',
      }}>
        <div style={{
          textAlign: 'center',
          marginBottom: '2.5rem',
          paddingBottom: '2rem',
          borderBottom: `2px solid ${COLORS.border}`,
        }}>
          <img 
            src="https://static.wixstatic.com/media/e78d43_e6a2a4d725014972851fe9dd56aeaa4b~mv2.png"
            alt="Virbal"
            style={{ maxWidth: '200px', marginBottom: '1rem' }}
          />
          <p style={{
            color: COLORS.textLight,
            fontStyle: 'italic',
            fontSize: '1rem',
            fontFamily: 'Inter, sans-serif',
          }}>
            Complete Your Signup
          </p>
        </div>

        {error && (
          <div style={{
            background: '#FFF5F5',
            border: '1px solid #FED7D7',
            color: COLORS.error,
            padding: '1rem',
            borderRadius: '6px',
            marginBottom: '1.5rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem',
            fontFamily: 'Inter, sans-serif',
            fontSize: '0.95rem',
          }}>
            <span style={{
              background: COLORS.error,
              color: 'white',
              width: '24px',
              height: '24px',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 'bold',
              flexShrink: 0,
            }}>!</span>
            {error}
          </div>
        )}

        {/* Step 1: Verification */}
        {step === 1 && (
          <form onSubmit={handleVerificationSubmit}>
            <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
              <h2 style={{
                fontSize: '1.75rem',
                color: COLORS.text,
                marginBottom: '0.75rem',
                fontWeight: 400,
              }}>
                Verify Your Number
              </h2>
              <p style={{
                color: COLORS.textLight,
                fontFamily: 'Inter, sans-serif',
                fontSize: '1rem',
              }}>
                Enter the 6-digit code sent to your phone
              </p>
            </div>

            <div style={{ marginBottom: '2rem' }}>
              <label htmlFor="verificationCode" style={{
                display: 'block',
                fontSize: '0.95rem',
                color: COLORS.text,
                marginBottom: '0.5rem',
                fontFamily: 'Inter, sans-serif',
                fontWeight: 500,
              }}>
                Verification Code
              </label>
              <input
                type="text"
                inputMode="numeric"
                id="verificationCode"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
                maxLength="6"
                required
                style={{
                  width: '100%',
                  padding: '0.875rem 1rem',
                  border: `2px solid ${COLORS.border}`,
                  borderRadius: '6px',
                  fontSize: '1.5rem',
                  textAlign: 'center',
                  letterSpacing: '0.5em',
                  fontWeight: 600,
                  fontFamily: 'Courier New, monospace',
                }}
              />
            </div>

            <button
              type="submit"
              style={{
                width: '100%',
                padding: '1rem 2rem',
                background: COLORS.primary,
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                fontSize: '1.05rem',
                fontWeight: 600,
                fontFamily: 'Inter, sans-serif',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
              }}
              onMouseEnter={(e) => e.target.style.background = COLORS.primaryLight}
              onMouseLeave={(e) => e.target.style.background = COLORS.primary}
            >
              Verify & Continue
            </button>
          </form>
        )}

        {/* Step 2: Questions */}
        {step === 2 && (
          <form onSubmit={handleQuestionsSubmit}>
            <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
              <h2 style={{
                fontSize: '1.75rem',
                color: COLORS.text,
                marginBottom: '0.75rem',
                fontWeight: 400,
              }}>
                Tell Us About Your Relationship
              </h2>
              <p style={{
                color: COLORS.textLight,
                fontFamily: 'Inter, sans-serif',
                fontSize: '1rem',
                lineHeight: 1.6,
                maxWidth: '600px',
                margin: '0 auto',
              }}>
                These 20 questions will help us get to know you and what is important to you for the growth of your relationship. It should only take 10-15min to complete but the investment will be 100 fold!
              </p>
            </div>

            {loadingQuestions ? (
              <div style={{ textAlign: 'center', padding: '3rem 0' }}>
                <div style={{
                  width: '50px',
                  height: '50px',
                  border: `4px solid ${COLORS.border}`,
                  borderTop: `4px solid ${COLORS.primary}`,
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite',
                  margin: '0 auto 1rem',
                }}></div>
                <p style={{ color: COLORS.textLight, fontFamily: 'Inter, sans-serif' }}>
                  Loading questions...
                </p>
              </div>
            ) : (
              <div style={{ marginBottom: '2rem' }}>
                {questions.map((question, index) => (
                  <div key={question.id} style={{
                    marginBottom: '1.75rem',
                    padding: '1.5rem',
                    background: 'rgba(45, 80, 22, 0.02)',
                    borderRadius: '6px',
                    border: `1px solid ${COLORS.border}`,
                  }}>
                    <label htmlFor={`question-${question.id}`} style={{
                      display: 'block',
                      fontSize: '1.05rem',
                      color: COLORS.text,
                      marginBottom: '1rem',
                      lineHeight: 1.6,
                    }}>
                      <span style={{
                        display: 'inline-block',
                        background: COLORS.primary,
                        color: 'white',
                        width: '28px',
                        height: '28px',
                        borderRadius: '50%',
                        textAlign: 'center',
                        lineHeight: '28px',
                        fontSize: '0.85rem',
                        fontWeight: 600,
                        fontFamily: 'Inter, sans-serif',
                        marginRight: '0.75rem',
                      }}>
                        {index + 1}
                      </span>
                      {question.question_text}
                      {question.required && (
                        <span style={{ color: COLORS.primary, marginLeft: '0.25rem' }}>*</span>
                      )}
                    </label>
                    {renderQuestionInput(question)}
                  </div>
                ))}
              </div>
            )}

            <button
              type="submit"
              disabled={loading || loadingQuestions}
              style={{
                width: '100%',
                padding: '1rem 2rem',
                background: loading ? COLORS.border : COLORS.primary,
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                fontSize: '1.05rem',
                fontWeight: 600,
                fontFamily: 'Inter, sans-serif',
                cursor: loading ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s ease',
                opacity: loading ? 0.6 : 1,
              }}
              onMouseEnter={(e) => !loading && (e.target.style.background = COLORS.primaryLight)}
              onMouseLeave={(e) => !loading && (e.target.style.background = COLORS.primary)}
            >
              {loading ? 'Completing Signup...' : 'Complete Signup'}
            </button>
          </form>
        )}
      </div>

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default CompleteSignup;
